import type { RetryableFunction } from '../interfaces/RetryableFunction';
import type { RetryOptions } from '../interfaces/RetryOptions';
import type { AsyncResult, AppError } from '../../error/types';
import { err, ok } from '../../error/functions';

const DEFAULT_OPTIONS: Omit<Required<RetryOptions>, 'getDelayMs'> = {
  maxAttempts: 3,
  initialDelay: 1000, // 1 second
  maxDelay: 10000,    // 10 seconds
  factor: 2,
  retryableErrors: [
    'ECONNRESET',
    'ETIMEDOUT',
    'ECONNREFUSED',
    'EPIPE',
    'EHOSTUNREACH',
    'ENETUNREACH',
    'ENOTFOUND',
    429, // Too Many Requests (e.g. GitHub rate limit)
    500,
    502,
    503,
    504
  ]
};

/**
 * Determines if an error should trigger a retry based on configuration.
 * Checks error.status first (preferred for HTTP errors including @octokit/request-error),
 * then error.code for network-level errors (e.g. ECONNRESET, ETIMEDOUT),
 * then error.message as a fallback.
 */
function isRetryableError(error: any, retryableErrors: Array<string | number>): boolean {
  // HTTP status codes (check first to avoid deprecated error.code on Octokit errors)
  if (typeof error.status === 'number') {
    return retryableErrors.includes(error.status);
  }

  // Network-level error codes (e.g. ECONNRESET, ETIMEDOUT)
  if (typeof error.code === 'string' && retryableErrors.includes(error.code)) {
    return true;
  }

  // Fallback: check error message for known error code strings
  if (error.message && retryableErrors.some(code => 
    typeof code === 'string' && error.message.includes(code)
  )) {
    return true;
  }

  return false;
}

/**
 * @group Functions
 * @summary <blockquote>Executes a function with exponential backoff retry logic using the Result pattern.</blockquote>
 * @param fn - The async function to execute
 * @param options - Retry configuration options
 * @returns AsyncResult containing the successful result or an error if all retries fail
 * @example
 * ```typescript
 * const result = await withRetry(async () => {
 *   return await fetchData();
 * }, { maxAttempts: 5 });
 * 
 * result.match({
 *   ok: (data) => console.log('Success:', data),
 *   err: (error) => console.error('Failed after retries:', error)
 * });
 * ```
 */
export async function withRetry<T>(
  fn: RetryableFunction<T>,
  options: RetryOptions = {}
): AsyncResult<T, AppError> {
  const config = { ...DEFAULT_OPTIONS, ...options };
  let attempt = 1;
  let delay = config.initialDelay;

  while (attempt <= config.maxAttempts) {
    try {
      const result = await fn();
      return ok(result);
    } catch (error) {
      // If this was our last attempt, or error isn't retryable, return error
      if (
        attempt === config.maxAttempts ||
        !isRetryableError(error, config.retryableErrors)
      ) {
        const errPayload: AppError = {
          errorCode: 'RETRY_FAILED',
          errorMessage: error instanceof Error ? error.message : String(error),
          metadata: { thrownError: error }
        };
        // If thrown value is already an AppError-like object, caller may use it
        if (error && typeof error === 'object' && 'errorCode' in error && 'errorMessage' in error) {
          errPayload.metadata = { ...errPayload.metadata, domainError: error };
        }
        return err(errPayload);
      }

      const delayMs = config.getDelayMs?.(attempt, error) ?? delay;

      // Log retry attempt
      console.warn(
        `Attempt ${attempt} failed for operation. Retrying in ${delayMs}ms...`,
        { error, attempt, delay: delayMs }
      );

      // Wait for the calculated delay
      await new Promise(resolve => setTimeout(resolve, delayMs));

      // Calculate next delay with exponential backoff (for when getDelayMs is not used next time)
      delay = Math.min(delay * config.factor, config.maxDelay);
      attempt++;
    }
  }

  return err({
    errorCode: 'RETRY_EXHAUSTED',
    errorMessage: 'Retry attempts exhausted'
  });
} 